/**
 * Fraud & Security Queue — operational-queue spec helpers.
 * Case ID FRD-######. Decision ID DEC-FS-###### after confirmation.
 * Status: New | In Progress | Decided (while Archive move unfinished)
 * Source: Customer | Business | Support Team | System | Moderation | Compliance
 * Priority: Low | Medium | High (read-only; Flag Severity, or Medium for Compliance transfer)
 * Types: Post | Review | Business Reply | Message | Quote | Business Profile Item |
 *        Account | Licence | Badge | Ticket
 */

export const FRAUD_QUEUE_ROUTE = '/admin/fraud/alerts';

export const QUEUE_SOURCES = [
  'Customer',
  'Business',
  'Support Team',
  'System',
  'Moderation',
  'Compliance',
];

export const QUEUE_PRIORITIES = ['Low', 'Medium', 'High'];

export const QUEUE_CASE_TYPES = [
  'Post',
  'Review',
  'Business Reply',
  'Message',
  'Quote',
  'Business Profile Item',
  'Account',
  'Licence',
  'Badge',
  'Ticket',
];

export const QUEUE_STATUSES = ['New', 'In Progress', 'Decided'];
export const QUEUE_OWNER = 'Fraud & Security';

export const FRAUD_DECISION_FORM_ID = 'FORM-000043';
export const FRAUD_DECISION_FORM_DISPLAY = 'FORM-000043 — Fraud & Security Decision Form';
export const FRAUD_REQUEST_FORM_DISPLAY = 'FORM-000041 — Fraud & Security Information Request Form';
export const FRAUD_RESPONSE_FORM_DISPLAY = 'FORM-000042 — Fraud & Security Information Response Form';

export const CONFIRMED_SEVERITY_OPTIONS = ['Low', 'Medium', 'High'];
export const FINAL_DECISION_OPTIONS = [
  'No Breach Found',
  'Insufficient Evidence',
  'Final Warning',
  'Permanent Ban',
];

export const SUPPORT_TEAM_MEMBERS = [
  'Sarah Nguyen',
  'Alex Chen',
  'John Smith',
  'Mike Davis',
  'Emma Wilson',
];
export const CURRENT_STAFF_MEMBER = 'Sarah Nguyen';

const RECEIVED = '2026-09-13T09:00:00+10:00';

const dash = (value) => {
  if (value == null) return '—';
  const text = String(value).trim();
  return text === '' || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined'
    ? '—'
    : text;
};

export const formatFraudDateTime = (value) => {
  if (!value || value === '—') return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
};

/** Case details: full date, time and the time zone recorded with the value. */
export const formatFraudCaseDateTime = (value) => {
  const base = formatFraudDateTime(value);
  if (base === '—') return '—';
  const match = String(value).match(/([+-]\d{2}:\d{2}|Z)$/);
  if (!match) return base;
  const zone = match[1] === 'Z' ? 'UTC' : `UTC${match[1]}`;
  return `${base} (${zone})`;
};

export const formatAssignedDisplay = (raw) => {
  if (!raw || String(raw).toLowerCase() === 'unassigned' || String(raw).trim() === '—') {
    return 'Unassigned';
  }
  return (
    String(raw)
      .replace(/^Fraud\s*&\s*Security — /i, '')
      .replace(/^(Admin|Compliance|Support|Moderator|Owner) — /i, '')
      .replace(/^Admin: /, '')
      .replace(/\s*\([^)]*\)\s*$/, '')
      .split(' (')[0]
      .trim() || 'Unassigned'
  );
};

export const normalizeSupportTeam = (value) => formatAssignedDisplay(value);

export const normalizeQueueStatus = (status) => {
  const raw = String(status || '').trim();
  if (!raw) return 'New';
  if (raw === 'New') return 'New';
  if (
    raw === 'In Progress' ||
    raw === 'Under Review' ||
    raw === 'In Review' ||
    raw === 'Active' ||
    raw === 'Assigned'
  ) {
    return 'In Progress';
  }
  if (raw === 'Decided' || raw === 'Resolved' || raw === 'Closed' || raw === 'Archived') {
    return 'Decided';
  }
  return raw;
};

/** New ↔ unassigned; In Progress ↔ assigned Support Team Member. */
export const coerceStatusAndAssignment = (row = {}) => {
  let status = normalizeQueueStatus(row.status);
  const supportTeamMember = normalizeSupportTeam(
    row.supportTeamMember || row.assignedTo || row.supportTeam
  );
  const assigned = supportTeamMember !== 'Unassigned';
  if (status !== 'Decided') {
    if (assigned && status === 'New') status = 'In Progress';
    else if (!assigned && status === 'In Progress') status = 'New';
  }
  return {
    status,
    supportTeamMember: assigned ? supportTeamMember : 'Unassigned',
    assignedTo: assigned ? supportTeamMember : 'Unassigned',
  };
};

export const decisionIdFromCaseId = (caseId) => {
  const match = String(caseId || '').match(/(\d+)/);
  const n = match ? Number(match[1]) : Date.now() % 1000000;
  return `DEC-FS-${String(n).padStart(6, '0')}`;
};

const baseCase = (row) => {
  const accountSummaryOverride = row.accountSummary;
  const coerced = coerceStatusAndAssignment(row);
  const next = {
    owner: QUEUE_OWNER,
    receivedOn: RECEIVED,
    lastUpdated: RECEIVED,
    archiveId: null,
    handoffSucceeded: false,
    warningHistoryAvailable: true,
    hasPreviousFinalWarning: false,
    caseReason: '—',
    sourceFlagId: '—',
    transferredFromCaseId: '—',
    relatedSupportTicketId: '—',
    restrictionSummary: '—',
    restriction: {
      status: '—',
      appliedOn: '—',
      appliedBy: '—',
      endTime: '—',
    },
    protection: {
      action: 'Sign Out Active Sessions',
      note: '—',
      latestResult: '—',
    },
    sourceInformation: {
      source: row.source,
    },
    informationRequests: [],
    linkedRecords: [],
    evidence: [],
    auditLog: [
      {
        dateTime: RECEIVED,
        event: 'Case receipt',
        details: `System; ${row.source}; ${row.caseType}`,
        record: row.caseId,
      },
    ],
    decision: {
      confirmedSeverity: '',
      finalDecision: '',
      finalAccountAction: '—',
      evidenceSummary: '',
      decisionReason: '',
      decisionId: null,
      decidedBy: null,
      decidedOn: null,
      finalisedOn: null,
      confirmed: false,
    },
    finalisation: [],
    ...row,
    ...coerced,
  };
  next.accountSummary = {
    accountName: row.accountName,
    accountId: row.accountId,
    accountType: row.accountType,
    accountStatus: 'Active',
    verificationStatus: 'Verified',
    previousFinalWarning: '—',
    previousWarningDecisionId: '—',
    existingRestriction: '—',
    ...(accountSummaryOverride || {}),
  };
  return next;
};

const SAMPLE_CASES = [
  // Moderation transfer · Post · New · unassigned · High (= Flag Severity)
  baseCase({
    caseId: 'FRD-000101',
    source: 'Moderation',
    caseType: 'Post',
    caseReason: 'Suspected stolen / copyrighted images with fraud indicators',
    sourceFlagId: 'FLG-20491',
    transferredFromCaseId: 'MOD-000841',
    accountName: 'Werribee Plumbing Pty Ltd',
    accountId: 'BUS-9934',
    accountType: 'Business',
    accountAvatar: 'https://i.pravatar.cc/150?img=12',
    priority: 'High',
    status: 'New',
    assignedTo: 'Unassigned',
    decisionId: null,
    evidence: [
      {
        evidenceId: 'EVD-000101',
        type: 'Record content',
        evidence: 'Reported post content preserved at transfer',
        sourceRecord: 'POST-000841',
        evidenceDate: '2026-09-12T14:20:00+10:00',
        preservedOn: RECEIVED,
        status: 'Preserved',
      },
    ],
    linkedRecords: [
      {
        recordType: 'Moderation Case',
        recordId: 'MOD-000841',
        relationship: 'Transferred from',
        status: 'Closed — Transferred',
      },
    ],
    auditLog: [
      {
        dateTime: RECEIVED,
        event: 'Case receipt',
        details: 'System; Moderation; Post; MOD-000841',
        record: 'FRD-000101',
      },
      {
        dateTime: RECEIVED,
        event: 'Transfer received',
        details: 'Sarah Nguyen; Moderation → Fraud & Security; MOD-000841',
        record: 'FRD-000101',
      },
    ],
  }),
  // Compliance transfer · Licence · In Progress · Medium (Compliance transfer Priority)
  baseCase({
    caseId: 'FRD-000102',
    source: 'Compliance',
    caseType: 'Licence',
    caseReason: 'Suspected forged plumbing licence documents',
    sourceFlagId: '—',
    transferredFromCaseId: 'CMP-000218',
    relatedSupportTicketId: 'TKT-000440',
    accountName: 'RapidFix Plumbing Pty Ltd',
    accountId: 'BUS-4412',
    accountType: 'Business',
    accountAvatar: 'https://i.pravatar.cc/150?img=15',
    priority: 'Medium',
    status: 'In Progress',
    assignedTo: 'Sarah Nguyen',
    decisionId: null,
    evidence: [
      {
        evidenceId: 'EVD-000102',
        type: 'Supporting file',
        evidence: 'licence-scan.pdf',
        sourceRecord: '—',
        evidenceDate: '—',
        preservedOn: RECEIVED,
        status: 'Preserved',
      },
    ],
    linkedRecords: [
      {
        recordType: 'Compliance Case',
        recordId: 'CMP-000218',
        relationship: 'Transferred from',
        status: 'Closed — Transferred',
      },
      {
        recordType: 'Support Ticket',
        recordId: 'TKT-000440',
        relationship: 'Linked ticket',
        status: 'Open',
      },
    ],
    informationRequests: [
      {
        request: 'Provide original licence certificate',
        sentOn: '2026-09-10T10:00:00+10:00',
        dueDate: '2026-09-17T10:00:00+10:00',
        status: 'Awaiting Response',
      },
    ],
    auditLog: [
      {
        dateTime: RECEIVED,
        event: 'Case receipt',
        details: 'System; Compliance; Licence; CMP-000218',
        record: 'FRD-000102',
      },
      {
        dateTime: RECEIVED,
        event: 'Assigned',
        details: 'Manager; Sarah Nguyen; Compliance transfer intake',
        record: 'FRD-000102',
      },
    ],
  }),
  // Support Team Flag · Account · In Progress · High
  baseCase({
    caseId: 'FRD-000103',
    source: 'Support Team',
    caseType: 'Account',
    caseReason: 'Suspected account takeover reported from Support Ticket',
    sourceFlagId: 'FLG-90103',
    relatedSupportTicketId: 'TKT-000512',
    accountName: 'Daniel Morris',
    accountId: 'CUS-3349',
    accountType: 'Customer',
    accountAvatar: 'https://i.pravatar.cc/150?img=32',
    priority: 'High',
    status: 'In Progress',
    assignedTo: 'Alex Chen',
    decisionId: null,
    linkedRecords: [
      {
        recordType: 'Support Ticket',
        recordId: 'TKT-000512',
        relationship: 'Linked ticket',
        status: 'Open',
      },
    ],
    evidence: [
      {
        evidenceId: 'EVD-000103',
        type: 'Record content',
        evidence: 'Customer takeover report details',
        sourceRecord: 'TKT-000512',
        evidenceDate: RECEIVED,
        preservedOn: RECEIVED,
        status: 'Preserved',
      },
    ],
  }),
  // System Flag · Account · Decided (pending Archive) · Medium
  baseCase({
    caseId: 'FRD-000104',
    source: 'System',
    caseType: 'Account',
    caseReason: 'Suspected account takeover — matching security alert',
    sourceFlagId: 'FLG-90104',
    accountName: 'Olivia M.',
    accountId: 'CUS-008821',
    accountType: 'Customer',
    accountAvatar: 'https://i.pravatar.cc/150?img=47',
    priority: 'Medium',
    status: 'Decided',
    assignedTo: 'Sarah Nguyen',
    decisionId: 'DEC-FS-000104',
    evidence: [
      {
        evidenceId: 'EVD-000104',
        type: 'Record content',
        evidence: 'Successful access with matching security alert',
        sourceRecord: 'SEC-000315',
        evidenceDate: RECEIVED,
        preservedOn: RECEIVED,
        status: 'Preserved',
      },
    ],
    decision: {
      confirmedSeverity: 'Medium',
      finalDecision: 'No Breach Found',
      finalAccountAction: '—',
      evidenceSummary: 'System event reviewed. No breach established.',
      decisionReason: 'Recorded findings do not support a breach.',
      decisionId: 'DEC-FS-000104',
      decidedBy: 'Sarah Nguyen',
      decidedOn: RECEIVED,
      finalisedOn: RECEIVED,
      confirmed: true,
    },
    finalisation: [
      {
        step: 'Outcome notice',
        result: 'Available to the affected account',
        record: 'DEC-FS-000104',
        lastAttempt: RECEIVED,
        completed: RECEIVED,
        status: 'Complete',
      },
    ],
    auditLog: [
      {
        dateTime: RECEIVED,
        event: 'Case receipt',
        details: 'System; System; Account',
        record: 'FRD-000104',
      },
      {
        dateTime: RECEIVED,
        event: 'Decision confirmation',
        details: 'Sarah Nguyen; No Breach Found; DEC-FS-000104',
        record: 'DEC-FS-000104',
      },
      {
        dateTime: RECEIVED,
        event: 'Case becoming Decided',
        details: 'Sarah Nguyen; In Progress → Decided',
        record: 'FRD-000104',
      },
    ],
  }),
  // Customer User Flag · Message · New · Low
  baseCase({
    caseId: 'FRD-000105',
    source: 'Customer',
    caseType: 'Message',
    caseReason: 'Scam payment request in message thread',
    sourceFlagId: 'FLG-50105',
    accountName: 'Bright Spark Electrical',
    accountId: 'BUS-5502',
    accountType: 'Business',
    accountAvatar: 'https://i.pravatar.cc/150?img=28',
    priority: 'Low',
    status: 'New',
    assignedTo: 'Unassigned',
    decisionId: null,
  }),
  // Business User Flag · Quote · In Progress · Medium
  baseCase({
    caseId: 'FRD-000106',
    source: 'Business',
    caseType: 'Quote',
    caseReason: 'Customer attempting off-platform payment fraud',
    sourceFlagId: 'FLG-55106',
    accountName: 'Jordan P.',
    accountId: 'CUS-9201',
    accountType: 'Customer',
    accountAvatar: 'https://i.pravatar.cc/150?img=45',
    priority: 'Medium',
    status: 'In Progress',
    assignedTo: 'John Smith',
    decisionId: null,
  }),
  // System · Business Profile Item · New · Medium
  baseCase({
    caseId: 'FRD-000107',
    source: 'System',
    caseType: 'Business Profile Item',
    caseReason: 'Security match on profile contact details pattern',
    sourceFlagId: 'FLG-58007',
    accountName: 'QuickFlow Plumbing Services',
    accountId: 'BUS-6609',
    accountType: 'Business',
    accountAvatar: 'https://i.pravatar.cc/150?img=60',
    priority: 'Medium',
    status: 'New',
    assignedTo: 'Unassigned',
    decisionId: null,
  }),
  // System · Review · In Progress · High
  baseCase({
    caseId: 'FRD-000108',
    source: 'System',
    caseType: 'Review',
    caseReason: 'Coordinated fake review pattern with security indicators',
    sourceFlagId: 'FLG-62008',
    accountName: 'Emma Davis',
    accountId: 'CUS-4102',
    accountType: 'Customer',
    accountAvatar: 'https://i.pravatar.cc/150?img=33',
    priority: 'High',
    status: 'In Progress',
    assignedTo: 'Mike Davis',
    decisionId: null,
  }),
];

const cloneCase = (row) => JSON.parse(JSON.stringify(row));

let queueState = SAMPLE_CASES.map(cloneCase);

export const collectQueueCases = () =>
  queueState
    .filter((row) => !row.handoffSucceeded)
    .map((row) => {
      const coerced = coerceStatusAndAssignment(row);
      return cloneCase({ ...row, ...coerced });
    })
    .sort(
      (a, b) =>
        new Date(b.receivedOn || 0).getTime() - new Date(a.receivedOn || 0).getTime()
    );

export const findQueueCase = (caseId) => {
  if (!caseId) return null;
  const found = queueState.find(
    (row) => String(row.caseId) === String(caseId) && !row.handoffSucceeded
  );
  if (!found) return null;
  const coerced = coerceStatusAndAssignment(found);
  return cloneCase({ ...found, ...coerced });
};

export const upsertQueueCase = (next) => {
  if (!next?.caseId) return null;
  const coerced = coerceStatusAndAssignment(next);
  const saved = cloneCase({ ...next, ...coerced });
  const index = queueState.findIndex((row) => String(row.caseId) === String(next.caseId));
  if (index >= 0) queueState[index] = saved;
  else queueState.push(saved);
  return cloneCase(saved);
};

export const markQueueHandoffSuccess = (caseId) => {
  const index = queueState.findIndex((row) => String(row.caseId) === String(caseId));
  if (index < 0) return null;
  queueState[index] = { ...queueState[index], handoffSucceeded: true };
  return cloneCase(queueState[index]);
};

export const generateDummyCases = () =>
  collectQueueCases().map((row) => ({
    id: row.caseId,
    caseId: row.caseId,
    accountDisplayName: row.accountName,
    accountId: row.accountId,
    accountAvatar: row.accountAvatar,
    primaryEntityType: row.accountType,
    status: row.status,
    riskLevel: row.priority,
    openedDate: row.receivedOn,
    lastActivity: row.lastUpdated,
    source: row.source,
    caseType: row.caseType,
    caseReason: row.caseReason,
    supportTeamMember: row.supportTeamMember,
    decisionId: row.decisionId,
  }));

export const matchesQueueSearch = (row, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.caseId,
    row.accountName,
    row.accountId,
    row.sourceFlagId,
    row.transferredFromCaseId,
    row.caseReason,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

export const matchesQueueFilters = (row, filters = {}) => {
  if (filters.status && filters.status !== 'All' && row.status !== filters.status) return false;
  if (filters.priority && filters.priority !== 'All' && row.priority !== filters.priority) {
    return false;
  }
  if (filters.supportTeam && filters.supportTeam !== 'All') {
    const member = normalizeSupportTeam(row.supportTeamMember || row.assignedTo);
    if (filters.supportTeam === 'Unassigned') {
      if (member !== 'Unassigned') return false;
    } else if (member !== filters.supportTeam) {
      return false;
    }
  }
  return true;
};

export const getCaseStatusFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...QUEUE_STATUSES.map((value) => ({ value, label: value })),
];

export const getPriorityFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...QUEUE_PRIORITIES.map((value) => ({ value, label: value })),
];

export const getSupportTeamFilterOptions = (rows = []) => {
  const names = new Set(['Unassigned']);
  rows.forEach((row) => {
    const name = normalizeSupportTeam(row.supportTeamMember || row.assignedTo);
    if (name && name !== 'Unassigned') names.add(name);
  });
  SUPPORT_TEAM_MEMBERS.forEach((name) => names.add(name));
  return [
    { value: 'All', label: 'All' },
    ...[...names].sort().map((value) => ({ value, label: value })),
  ];
};

/** @deprecated Prefer getCaseStatusFilterOptions / getPriorityFilterOptions */
export const getSourceFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...QUEUE_SOURCES.map((value) => ({ value, label: value })),
];

export const getTypeFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...QUEUE_CASE_TYPES.map((value) => ({ value, label: value })),
];

export const getStatusFilterOptions = getCaseStatusFilterOptions;

export const finalAccountActionFor = (decision) => {
  if (decision === 'Final Warning') return 'Final Warning';
  if (decision === 'Permanent Ban') return 'Permanent Ban';
  return '—';
};

export const display = (value) => dash(value);

export const nextPrefixedId = (prefix, existing = [], width = 6) => {
  const nums = existing.map((id) => Number(String(id || '').replace(/\D/g, '')) || 0);
  return `${prefix}-${String(Math.max(0, ...nums) + 1).padStart(width, '0')}`;
};

export const isAssignedMember = (value) => {
  const name = formatAssignedDisplay(value);
  return Boolean(name && name !== 'Unassigned');
};

export const isDuringReview = (row) => {
  if (!row || row.decision?.confirmed) return false;
  return (
    row.status === 'In Progress' &&
    isAssignedMember(row.supportTeamMember || row.assignedTo)
  );
};

export const canAddEvidence = (row) => {
  if (!row || row.decision?.confirmed) return false;
  return isDuringReview(row);
};

export const hasOpenRequest = (row) =>
  (row?.informationRequests || []).some(
    (item) => item.status === 'Awaiting Response' || item.status === 'Issued'
  );

export const hasCompletedRequest = (row) =>
  (row?.informationRequests || []).some(
    (item) => item.status === 'Information Received' || item.status === 'No Response / Expired'
  );

export const restrictionSatisfied = () => true;

export const protectionSatisfied = () => true;

export const requestRequirementsMet = (row) => !hasOpenRequest(row);

export const decisionIsPermitted = (_row, decision) => FINAL_DECISION_OPTIONS.includes(decision);

export const finalisationStepsFor = (decision, decisionId) => {
  const notice = {
    step: 'Outcome notice',
    result: '—',
    record: decisionId || '—',
    lastAttempt: '—',
    completed: '—',
    status: 'Unfinished',
  };
  if (decision === 'Final Warning') {
    return [
      {
        step: 'Final Warning',
        result: '—',
        record: decisionId || '—',
        lastAttempt: '—',
        completed: '—',
        status: 'Unfinished',
      },
      notice,
    ];
  }
  if (decision === 'Permanent Ban') {
    return [
      {
        step: 'Permanent Ban',
        result: '—',
        record: decisionId || '—',
        lastAttempt: '—',
        completed: '—',
        status: 'Unfinished',
      },
      notice,
    ];
  }
  return [notice];
};

export const restrictionEndTime = (appliedOn, duration) => {
  if (!appliedOn || appliedOn === '—') return '—';
  const start = new Date(appliedOn);
  if (Number.isNaN(start.getTime())) return '—';
  if (duration === '24 Hours') {
    return new Date(start.getTime() + 24 * 60 * 60 * 1000).toISOString();
  }
  const days = {
    '3 Calendar Days': 3,
    '7 Calendar Days': 7,
    '14 Calendar Days': 14,
    '30 Calendar Days': 30,
  };
  const count = days[duration];
  if (!count) return '—';
  start.setDate(start.getDate() + count);
  return start.toISOString();
};

export const assignToMeUnavailableReason = (row) => {
  if (!row) return 'Case is not available.';
  if (row.status !== 'New') return 'Assign to Me is available only for a New case.';
  if (isAssignedMember(row.supportTeamMember || row.assignedTo)) {
    return 'This case is already assigned.';
  }
  return null;
};

/** @deprecated Prefer assignToMeUnavailableReason — opening a case does not assign. */
export const startCaseUnavailableReason = (row) => {
  if (!row) return 'Case is not available.';
  if (row.status !== 'New') return 'Assign to Me is available only for a New case.';
  return null;
};
